import log from 'loglevel'
import React, { useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { SurveyDetailItem } from '../api/models/SurveyDetailItem'
import {
  createNewQuestion,
  fetchSurveyById,
  getSurveyAccess,
  removeQuestion,
  setSurveyAccess,
  updateQuestion,
} from '../api/surveyApi'
import { Box } from '../components/Box'
import { Button, ButtonState } from '../components/Button'
import { Spinner } from '../components/icons/Spinner'
import { SettingsPanel } from '../components/SettingsPanel'
import { useCookie } from '../hooks/useCookie'
import { access } from '@inrupt/solid-client'

export function FormBuilder() {
  const history = useHistory()
  const textAreaRef = useRef<HTMLElement>(null)
  const [containerUrl] = useCookie('containerUrl')
  const { surveyId } = useParams<{ surveyId: string }>()
  const [status, setStatus] = React.useState<ButtonState>('idle')
  const [
    loadingSurveyStatus,
    setLoadingSurveyStatus,
  ] = React.useState<ButtonState>('idle')
  const [
    changeAccessStatus,
    setChangeAccessStatus,
  ] = React.useState<ButtonState>('idle')
  const [surveyItem, setSurveyItem] = React.useState<
    SurveyDetailItem | undefined
  >(undefined)
  const [surveyUrl, setSurveyUrl] = React.useState<string>('')
  const [shareUrl, setShareUrl] = React.useState<string>('')
  const [toggleActive, setToggleActive] = React.useState<boolean>(false)

  React.useEffect(() => {
    refreshSurvey(surveyId).then(() => fetchSurveyAccess(surveyId))
  }, [surveyId])

  function refreshSurvey(surveyId: string) {
    log.debug('refreshing survey...')
    setLoadingSurveyStatus('pending')
    return fetchSurveyById(surveyId).then(
      item => {
        const url = `${containerUrl}${item.fileName}`
        const share = `${
          process.env.REACT_APP_SURVEY_APP
        }/response?link=${encodeURIComponent(url)}`
        setSurveyItem(item)
        setSurveyUrl(url)
        setShareUrl(share)
        setLoadingSurveyStatus('resolved')
      },
      e => {
        log.error({ err: e })
        setLoadingSurveyStatus('rejected')
      }
    )
  }

  function fetchSurveyAccess(surveyId: string) {
    setChangeAccessStatus('pending')
    return getSurveyAccess(surveyId).then(
      (a: access.Access) => {
        log.debug({ access: a })
        setToggleActive(a.read)
        setChangeAccessStatus('resolved')
      },
      e => {
        log.error({ fetchSurveyAccessError: e })
        setChangeAccessStatus('rejected')
      }
    )
  }

  function changePublicAccess(isPublic: boolean): Promise<void> {
    if (changeAccessStatus === 'pending') {
      return Promise.resolve()
    }
    setChangeAccessStatus('pending')
    return setSurveyAccess(surveyId, isPublic).then(
      () => {
        setChangeAccessStatus('resolved')
        setToggleActive(isPublic)
      },
      () => {
        setChangeAccessStatus('rejected')
        setToggleActive(!isPublic)
      }
    )
  }

  async function handleAddQuestion(): Promise<void> {
    setStatus('pending')
    return createNewQuestion(surveyId)
      .then(() => refreshSurvey(surveyId))
      .then(
        () => setStatus('resolved'),
        () => setStatus('rejected')
      )
  }

  function handleUpdateQuestion(
    event: React.FocusEvent<HTMLInputElement>,
    questionUrl: string
  ): Promise<void> {
    setStatus('pending')
    return updateQuestion({
      id: surveyId,
      questionUrl,
      text: event.target.value,
    }).then(
      () => {
        setStatus('resolved')
      },
      e => {
        log.error({ err: e })
        setStatus('rejected')
      }
    )
  }

  function handleRemoveQuestion(questionUrl: string): Promise<void> {
    setStatus('pending')
    return removeQuestion({
      id: surveyId,
      questionUrl,
    })
      .then(() => refreshSurvey(surveyId))
      .then(
        () => {
          setStatus('resolved')
        },
        e => {
          log.error({ err: e })
          setStatus('rejected')
        }
      )
  }

  return (
    <>
      <SettingsPanel className="mb-8">
        {loadingSurveyStatus === 'resolved' ? (
          <>
            <div className="px-4 py-5 sm:px-6 flex flex-row items-center ">
              <span className="flex-1 text-gray-800">
                Your Survey is hosted at{' '}
              </span>
              <span className="text-sm text-gray-500">{surveyUrl}</span>
            </div>

            <div className="px-4 py-5 sm:px-6 flex flex-row items-center">
              <span className="flex-1 text-gray-800">
                Your Survey is{' '}
                <strong>{toggleActive ? 'public' : 'private'}</strong>
              </span>
              <span className="text-sm text-gray-500">
                <div
                  className={`flex justify-between items-center ${
                    changeAccessStatus !== 'pending' ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => changePublicAccess(!toggleActive)}
                >
                  <div
                    className={`${
                      toggleActive ? 'bg-green-500' : 'bg-red-500'
                    } ${
                      changeAccessStatus === 'pending' ? 'opacity-50' : ''
                    } w-12 h-6 flex items-center rounded-full p-1 duration-300 ease-in-out `}
                  >
                    <div
                      className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                        toggleActive ? 'translate-x-6' : ''
                      } ${
                        changeAccessStatus === 'pending' ? 'opacity-50' : ''
                      }`}
                    />
                  </div>
                </div>
              </span>
            </div>

            {toggleActive ? (
              <div className="px-4 py-5 sm:px-6 text-sm flex flex-row items-center">
                {/*<div className="underline">Share this link </div>*/}
                <div className="flex-1 italic text-gray-500" id="share-link">
                  {shareUrl}
                </div>
                <Button
                  onClick={() => {
                    void navigator.clipboard.writeText(shareUrl)
                  }}
                >
                  Copy link
                </Button>
              </div>
            ) : (
              <div className="px-4 py-5 sm:px-6 text-sm flex flex-row items-center">
                <div className="flex-1 italic text-gray-500">
                  Make your survey <strong>public</strong> to get a shareable
                  link
                </div>
                <Button disabled>Copy link</Button>
              </div>
            )}
          </>
        ) : null}
      </SettingsPanel>

      <div className="mb-8">
        <a
          onClick={() => history.push('/surveys')}
          className="text-sm text-gray-400 sm:px-6 px-4 mb-8 cursor-pointer"
        >
          &lt; Go back
        </a>
      </div>

      {loadingSurveyStatus === 'pending' ? (
        <div className="px-4 py-5 sm:px-6 flex flex-row items-center ">
          <Spinner />
        </div>
      ) : (
        <Box padding>
          <form>
            <div className="flex flex-row items-center">
              <Button
                onClick={handleAddQuestion}
                disabled={!surveyItem}
                className="mr-4"
              >
                Add Question
              </Button>
              {status === 'pending' ? (
                <span className="text-gray-400 italic">Saving...</span>
              ) : (
                <span className="text-gray-400 italic">Saved</span>
              )}

              <span className="flex-auto" />
            </div>

            {surveyItem?.questions.map((q, index) => (
              <div className="py-5 flex flex-col" key={q.url}>
                <label
                  className="text-sm font-medium text-gray-500 pb-1"
                  htmlFor={`question-${q.url}`}
                >
                  Question {index + 1}
                </label>
                <div className="flex flex-row">
                  <input
                    className="border py-2 px-3 text-grey-darkest flex-auto rounded"
                    type="text"
                    name={`question-${q.url}`}
                    id={`question-${q.url}`}
                    placeholder={q.url}
                    defaultValue={q.text}
                    onBlur={e => handleUpdateQuestion(e, q.url)}
                  />
                  <Button
                    onClick={() => handleRemoveQuestion(q.url)}
                    color="gray"
                    className="ml-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="white"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10.185,1.417c-4.741,0-8.583,3.842-8.583,8.583c0,4.74,3.842,8.582,8.583,8.582S18.768,14.74,18.768,10C18.768,5.259,14.926,1.417,10.185,1.417 M10.185,17.68c-4.235,0-7.679-3.445-7.679-7.68c0-4.235,3.444-7.679,7.679-7.679S17.864,5.765,17.864,10C17.864,14.234,14.42,17.68,10.185,17.68 M10.824,10l2.842-2.844c0.178-0.176,0.178-0.46,0-0.637c-0.177-0.178-0.461-0.178-0.637,0l-2.844,2.841L7.341,6.52c-0.176-0.178-0.46-0.178-0.637,0c-0.178,0.176-0.178,0.461,0,0.637L9.546,10l-2.841,2.844c-0.178,0.176-0.178,0.461,0,0.637c0.178,0.178,0.459,0.178,0.637,0l2.844-2.841l2.844,2.841c0.178,0.178,0.459,0.178,0.637,0c0.178-0.176,0.178-0.461,0-0.637L10.824,10z"></path>
                    </svg>
                  </Button>
                </div>
              </div>
            ))}
          </form>
        </Box>
      )}
    </>
  )
}
